/**
 * Send chat messages to the agent: HTTP (snail) and WebSocket payload builder.
 * HTTP: POST /_chat/tb — returns full response, UI typically does refresh_chat.
 * WebSocket: same payload shape with auth + data, sent over WS for real-time streaming.
 */

import { getAuthHeaders } from '../core';
import { config } from '../../config';
import type { ChatMessagePayloadBase } from './build_chat_payload';

const CHAT_TB_PATH = '/_chat/tb';

export interface SendChatMessageHttpResult {
  ok: boolean;
  data?: unknown;
  error?: string;
}

/**
 * Sends a chat message via HTTP (snail). POST to apiBase/_chat/tb.
 * Response is the agent result; caller can pass it to messageUp({ type: 'refresh_chat', response }) to refresh the UI.
 */
export async function sendChatMessageHttp(
  message: string,
  payload: ChatMessagePayloadBase
): Promise<SendChatMessageHttpResult> {
  const apiBase = (config.apiBase || '').trim();
  if (!apiBase) {
    return { ok: false, error: 'Missing API config (apiBase). Set REACT_APP_API_BASE in .env.' };
  }
  const url = `${apiBase}${CHAT_TB_PATH}`;
  const body = { ...payload, data: message };

  try {
    const headers = await getAuthHeaders();
    const res = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        ok: false,
        error: (data?.error as string) || (data?.message as string) || `HTTP ${res.status}`,
        data,
      };
    }
    return { ok: true, data };
  } catch (e) {
    const error = e instanceof Error ? e.message : String(e);
    return { ok: false, error };
  }
}

/**
 * Returns the WebSocket URL for the agent from config (REACT_APP_WEBSOCKET_URL).
 * Use this to open a WebSocket; then send messages with buildAgentWsMessage + ws.send(JSON.stringify(...)).
 */
export function getAgentWebSocketUrl(): string {
  const url = (config.websocketUrl || '').trim();
  if (!url) throw new Error('Missing WebSocket URL. Set REACT_APP_WEBSOCKET_URL in .env.');
  return url;
}

export interface AgentWsMessage {
  action: string;
  data: unknown;
  auth: string;
  entity_type?: string;
  entity_id?: string;
  thread?: string | null;
  portfolio?: string;
  org?: string;
  next?: string | null;
  core?: string;
  [key: string]: unknown;
}

function toChatData(message: unknown): { role: 'user'; content: string } {
  if (message && typeof message === 'object') {
    const maybe = message as { role?: unknown; content?: unknown };
    if (typeof maybe.role === 'string' && typeof maybe.content === 'string') {
      return { role: 'user', content: maybe.content };
    }
    return { role: 'user', content: JSON.stringify(message) };
  }
  return { role: 'user', content: String(message ?? '') };
}

/**
 * Builds the object to send over the WebSocket (JSON.stringify and ws.send).
 * Matches the payload expected by POST /_chat/message (socket_auth_required).
 */
export function buildAgentWsMessage(
  message: unknown,
  payload: ChatMessagePayloadBase,
  accessToken: string
): AgentWsMessage {
  const core = payload.core ?? 'default';
  return {
    action: payload.action,
    data: toChatData(message),
    auth: accessToken,
    entity_type: payload.entity_type,
    entity_id: payload.entity_id,
    thread: payload.thread ?? undefined,
    portfolio: payload.portfolio,
    org: payload.org,
    next: payload.next ?? undefined,
    core,
  };
}

/**
 * Sends a message over an open WebSocket. Builds payload with buildAgentWsMessage and sends.
 * Use getAuthHeaders() or getAccessToken() to pass the token. Returns true if sent, false if socket not open.
 */
export async function sendChatMessageWs(
  ws: WebSocket,
  message: unknown,
  payload: ChatMessagePayloadBase,
  getAccessToken: () => Promise<string>
): Promise<boolean> {
  if (ws.readyState !== WebSocket.OPEN) return false;
  const token = await getAccessToken();
  const msg = buildAgentWsMessage(message, payload, token);
  ws.send(JSON.stringify(msg));
  return true;
}
